import {
    collection,
    doc,
    getDoc,
    addDoc,
    updateDoc,
    query,
    where,
    getDocs,
    getCountFromServer,
    orderBy,
    startAfter,
    limit,
    endBefore,
    limitToLast,
    Timestamp,
    Firestore,
    QueryConstraint,
    QueryLimitConstraint,
    QueryOrderByConstraint,
    QueryStartAtConstraint,
    documentId,
    or,
    QueryCompositeFilterConstraint,
    QuerySnapshot,
    DocumentData
} from 'firebase/firestore';
import { utils, write } from 'xlsx';
import { User } from 'firebase/auth';

// Types
interface UserData {
    id: string;
    companyId?: string;
    // Add other user fields here
    [key: string]: any; // For other user fields
}

interface SubUserData {
    id: string;
    name: string;
    username: string;
    email: string;
    phone: string;
    isActive?: boolean;
}


// staff management
interface FetchCompanyUsersResponse {
    data: {
        users?: UserData[];
        subAgents?: SubUserData[];
        companyId: string;
    }
}

interface FetchStaffRolesResponse {
    data: {
        roles: { [key: string]: string };
    }
}

interface LicensesCheckResponse {
    data: {
        loginPostfix: string;
        licencesLimit: number;
        totalUsersCount: number;
        canAddMoreUsers: boolean;
        activeUsersCount: number;
    }
}

interface CompanyData {
    licenses?: number;
    licences?: number;
    [key: string]: any;
}

// function to fetch company users
export async function fetchCompanyUsers(
    db: Firestore,
    user: User | null
): Promise<FetchCompanyUsersResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch company users');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Query users with the same companyId
        const usersQuery = query(
            collection(db, 'users'),
            where('companyId', '==', userData.companyId)
        );

        const usersSnapshot = await getDocs(usersQuery);

        const usersList = usersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as UserData));

        return {
            data: {
                users: usersList,
                companyId: userData.companyId
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching company users';
        console.error('Error in fetchCompanyUsers:', error);
        throw new Error(errorMessage);
    }
}

// function to fetch sub agents
export async function fetchSubAgents(
    db: Firestore,
    user: User | null
): Promise<FetchCompanyUsersResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch sub agents');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Query sub agents from the companies collection
        const subAgentsQuery = query(
            collection(db, 'companies', userData.companyId, 'subAgents')
        );

        const usersSnapshot = await getDocs(subAgentsQuery);

        const usersList = usersSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as SubUserData));

        // sort users by name
        usersList.sort((a, b) => a.name.localeCompare(b.name));

        return {
            data: {
                subAgents: usersList,
                companyId: userData.companyId
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching sub agents';
        console.error('Error in fetchSubAgents:', error);
        throw new Error(errorMessage);
    }
}

// function to update sub agent
export async function updateSubAgent(
    db: Firestore,
    user: User | null,
    subAgentId: string,
    data: any
): Promise<{ success: boolean, message: string }> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can update sub agents');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Update sub agent data
        await updateDoc(doc(db, 'companies', userData.companyId, 'subAgents', subAgentId), data);

        return {
            success: true,
            message: 'Sub agent updated successfully'
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating sub agent';
        console.error('Error in updateSubAgent:', error);
        throw new Error(errorMessage);
    }
}

// function to fetch staff roles
export async function fetchStaffRoles(
    db: Firestore,
    user: User | null
): Promise<FetchStaffRolesResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch staff roles');
        }

        // Get all staff roles
        const staffRolesSnapshot = await getDocs(collection(db, 'staffRoles'));

        const roles: { [key: string]: string } = {};

        staffRolesSnapshot.forEach((doc) => {
            const docData = doc.data();
            // Try to find the role name in different possible field names
            const roleName = docData.roleName || docData.name || docData.role;
            if (roleName) {
                roles[doc.id] = roleName;
            }
        });

        return {
            data: {
                roles
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching staff roles';
        console.error('Error in fetchStaffRoles:', error);
        throw new Error(errorMessage);
    }
}

// function to check licenses limit
export async function checkLicensesLimit(
    db: Firestore,
    user: User | null
): Promise<LicensesCheckResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can check licence limits');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        const companyId = userData.companyId;

        // Get company document
        const companyDoc = await getDoc(doc(db, 'companies', companyId));

        if (!companyDoc.exists()) {
            throw new Error('Company not found');
        }

        const companyData = companyDoc.data() as CompanyData;

        // login_postfix of the specific company
        const loginPostfix = companyData?.login_postfix || '';

        // Check both possible spellings of licenses/licences
        const licenses = companyData?.licenses || companyData?.licences || 0;

        // Query total users count
        const totalUsersQuery = query(
            collection(db, 'users'),
            where('companyId', '==', companyId)
        );
        const totalUsersSnapshot = await getCountFromServer(totalUsersQuery);
        const totalUsersCount = totalUsersSnapshot.data().count;

        // Query active users count
        const activeUsersQuery = query(
            collection(db, 'users'),
            where('companyId', '==', companyId),
            where('isActive', '==', true)
        );
        const activeUsersSnapshot = await getCountFromServer(activeUsersQuery);
        const activeUsersCount = activeUsersSnapshot.data().count;

        return {
            data: {
                loginPostfix: loginPostfix,
                licencesLimit: licenses,
                totalUsersCount: totalUsersCount,
                canAddMoreUsers: totalUsersCount < licenses,
                activeUsersCount: activeUsersCount
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error checking licenses limit';
        console.error('Error in checkLicensesLimit:', error);
        throw new Error(errorMessage);
    }
}

// function to fetch departments
export async function fetchDepartments(
    db: Firestore,
    user: User | null
): Promise<{ data: { departments: { id: string, name: string }[] } }> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch sub agents');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // department query
        const departmentQuery = query(
            collection(db, 'companies', userData.companyId, 'departments')
        );

        // Get all departments
        const departmentsSnapshot = await getDocs(departmentQuery);

        const departments = departmentsSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().department
        }));

        return {
            data: {
                departments
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching departments';
        console.error('Error in fetchDepartments:', error);
        throw new Error(errorMessage);
    }
}

// function to fetch sub  agent permissions
export async function fetchSubAgentPermissions(
    db: Firestore,
    user: User | null
): Promise<{ data: { subAgentPermissions: { id: string, name: string }[] } }> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch sub agents');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // department query
        const departmentQuery = query(
            collection(db, 'companies', userData.companyId, 'subAgentPermissions')
        );

        // Get all subAgentPermissions
        const subAgentPermissionsSnapshot = await getDocs(departmentQuery);

        const subAgentPermissions = subAgentPermissionsSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().department
        }));

        return {
            data: {
                subAgentPermissions
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching subAgentPermissions';
        console.error('Error in fetchSubAgentPermissions:', error);
        throw new Error(errorMessage);
    }
}

// function to fetch pipelines from the subcollection leadPipelines
export async function fetchPipelines(
    db: Firestore,
    user: User | null
): Promise<{ data: { pipelines: { id: string, name: string }[] } }> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch pipelines');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Query pipelines from the leadPipelines subcollection
        const pipelinesQuery = query(
            collection(db, 'companies', userData.companyId, 'leadPipelines')
        );

        const pipelinesSnapshot = await getDocs(pipelinesQuery);

        const pipelines = pipelinesSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().pipelineName || doc.data().name || 'Unnamed Pipeline' // Fallback to 'Unnamed Pipeline' if no name field exists
        }));

        return {
            data: {
                pipelines
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching pipelines';
        console.error('Error in fetchPipelines:', error);
        throw new Error(errorMessage);
    }
}

// function to fetch filterButtons from the subcollection filterButtons
export async function fetchFilterButtons(
    db: Firestore,
    user: User | null
): Promise<{ data: { filterButtons: { id: string, name: string, color: string }[] } }> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch filter buttons');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Query filter buttons from the subcollection
        const filterButtonsQuery = query(
            collection(db, 'companies', userData.companyId, 'filterButtons')
        );

        const filterButtonsSnapshot = await getDocs(filterButtonsQuery);

        const filterButtons = filterButtonsSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().name,
            color: doc.data().color
        }));

        return {
            data: {
                filterButtons
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching filter buttons';
        console.error('Error in fetchFilterButtons:', error);
        throw new Error(errorMessage);
    }
}

// Dashboard
interface FetchChannelRequest {
    channel: string;  // Changed from params to be more descriptive
    isManager?: boolean;
}

interface FetchChannelOverviewResponse {
    data: {
        converted: number;
        total: number;
        convertedP: number;
    }
}

function getConversionPercentage(totalConverted: number, totalLeads: number): number {
    if (totalLeads === 0) {
        return 0;
    }
    return Number(((totalConverted / totalLeads) * 100).toFixed(2)); // Added number formatting
}

// fucntion to fetch channel overview
export async function fetchChannelOverview(
    db: Firestore,
    user: User | null,
    request: FetchChannelRequest
): Promise<FetchChannelOverviewResponse> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch channel overview');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        console.log("isMAnager", request.isManager);


        let usersQuery = query(
            collection(db, 'users'),
            where('companyId', '==', userData.companyId)
        );
        // if isManager is true get all the users in the company where the managerId is the current user id
        if (request.isManager === true) {
            usersQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData.companyId),
                where('managerId', '==', user.uid)
            );
        }

        const usersSnapshot = await getDocs(usersQuery);
        const userIds = usersSnapshot.docs.map(doc => doc.id);

        console.log("userIds", userIds?.length);


        // Query to get lead sources for the specified channel
        const leadSourcesQuery = query(
            collection(db, 'leadSources'),
            where('channel', '==', request.channel)
        );

        const leadSourcesSnapshot = await getDocs(leadSourcesQuery);

        if (leadSourcesSnapshot.empty) {
            return {
                data: {
                    converted: 0,
                    total: 0,
                    convertedP: 0
                }
            };
        }

        const leadSources = leadSourcesSnapshot.docs.map(doc => doc.id);

        // Query to get converted state ID
        const leadStatesQuery = query(
            collection(db, 'leadStates'),
            where('state', '==', 'Converted')
        );

        const leadStatesSnapshot = await getDocs(leadStatesQuery);

        if (leadStatesSnapshot.empty) {
            throw new Error('Converted lead state not found');
        }

        const convertedStateId = leadStatesSnapshot.docs[0].id;

        // Query for total leads
        let totalLeadsQuery

        if (request.isManager === true) {
            console.log("manager");

            totalLeadsQuery = query(
                collection(db, `companies/${userData.companyId}/leads`),
                where('assignedTo', 'in', userIds),
                where('sourceId', 'in', leadSources),
            );
        } else {
            console.log("not manager");

            totalLeadsQuery = query(
                collection(db, `companies/${userData.companyId}/leads`),
                where('sourceId', 'in', leadSources)
            );
        }

        console.log("totalLeadsQuery", await getDocs(totalLeadsQuery));

        const totalLeadsSnapshot = await getCountFromServer(totalLeadsQuery);
        const totalLeads = totalLeadsSnapshot.data().count;

        // Query for converted leads
        let totalConvertedQuery

        if (request.isManager === true) {
            totalConvertedQuery = query(
                collection(db, `companies/${userData.companyId}/leads`),
                where('assignedTo', 'in', userIds),
                where('sourceId', 'in', leadSources),
                where('stateId', '==', convertedStateId),
            );
        } else {
            totalConvertedQuery = query(
                collection(db, `companies/${userData.companyId}/leads`),
                where('sourceId', 'in', leadSources),
                where('stateId', '==', convertedStateId)
            );
        }

        const totalConvertedSnapshot = await getCountFromServer(totalConvertedQuery);
        const totalConverted = totalConvertedSnapshot.data().count;

        // Calculate conversion percentage
        const convertedPercentage = getConversionPercentage(totalConverted, totalLeads);

        return {
            data: {
                converted: totalConverted,
                total: totalLeads,
                convertedP: convertedPercentage
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching channel overview';
        console.error('Error in fetchChannelOverview:', error);
        throw new Error(errorMessage);
    }
}

// Leads management
interface FetchLeadsRequest {
    pageSize?: number;
    pageNumber?: number;  // Add pageNumber for supporting pagination
    lastDocumentId?: string;
    lastLeadDate?: Date;
    isActive?: boolean;
    sourceIds?: string[];
    stateIds?: string[];
    stageIds?: string[];
    statusIds?: string[];
    admissionStatusIds?: string[];
    welcomeCallStatusIds?: string[];
    visaProcessStatusIds?: string[];
    applicationStatusIds?: string[];
    conditionalOfferLetterStatusIds?: string[];
    unconditionalOfferLetterStatusIds?: string[];
    feePaymentStatusIds?: string[];
    fileSubmissionStatusIds?: string[];
    activeFollowUpIds?: string[];
    assignedTo?: string[] | null;
    subAgentId?: string | null;
    subAgent?: string[] | null;
    campaignIds?: string[];
    preferredStreamIds?: string[];
    superAgentId?: string[];
    admissionSubStatus?: string[];
    leadSubSources?: string[];
    leadSubStages?: string[];
    leadSubStatuses?: string[];
    destinationIds?: string[];
    preferredUniversityIds?: string[];
    startDate?: Timestamp | null;
    endDate?: Timestamp | null;
    tranffertAtStartDate?: Timestamp | null;
    tranffertAtEndDate?: Timestamp | null;
    updatedAtStartDate?: Timestamp | null;
    updatedAtEndDate?: Timestamp | null;
    followUpAtStartDate?: Timestamp | null;
    followUpAtEndDate?: Timestamp | null;
    searchValue?: string;
    phoneValue?: string;
    intakeYear?: string[];
    intakeMonth?: string[];
    // Pagination navigation additions
    direction?: 'next' | 'prev' | 'first';
    cursorId?: string;
    cursorType?: 'first';
    orderByField: 'createdAt' | 'updatedAt' | 'transferredAt';
    sortDirection?: 'asc' | 'desc';
}

// Interface for lead remarks
interface LeadRemark {
    id: string;
    remark: string;
    userId: string;
    userName: string;
    createdAt: Timestamp;
}

// Separate interface for raw Firestore data
interface LeadDocumentData {
    subAdmissionStatusId: string;
    subStatusId: string;
    preferredStreamId: string;
    fileSubmissionStatusId: string;
    feePaymentStatusId: string;
    conditionalOfferLetterStatusId: string;
    unconditionalOfferLetterStatusId: string;
    applicationMovedStatusId: string;
    visaFileProcessingStatusId: string;
    welcomeCallByAdmissionTeamStatusId: string;
    name: string;
    email?: string;
    number?: string;
    alternateNumber1?: string;
    alternateNumber2?: string;
    reversedNumber?: string;
    reversedAlternateNumber1?: string;
    reversedAlternateNumber2?: string;
    assignedTo: string;
    subAgentId?: string;
    subAgent?: string;
    adminNotes?: string;
    sourceId: string;
    subSourceId?: string | null;
    subStageId?: string;
    stateId: string;
    stageId: string;
    statusId: string;
    admissionStatusId: string;
    destinationId: string;
    createdBy?: string;
    updatedBy?: string;
    updatedAt: Timestamp;
    createdAt: Timestamp;
    isActive: boolean;
    isNewEntry?: boolean;
    transferredAt?: Timestamp | null;
    campaignId?: string;
    activeFollowUpId?: string | null;
    hasActiveFollowUp?: boolean;
    activeFollowUpText?: string | null;
    activeFollowUpTime?: Timestamp | null;
    pipelines: string[];
    intakeMonth: string;
    intakeYear: number;
    followUpDate?: Timestamp;
    superAgents?: string;
}

interface Destination {
    id: string
    name: string;
}

interface SubAgent {
    id: string;
    name: string;
    email: string;
    isActive: boolean;
}
// Interface for the final lead data with all fields
export interface LeadData extends LeadDocumentData {

    transferredAt: any;
    id: string;
    followUp?: Timestamp | null;
    followUpText?: string | null;
    followUpTime?: Timestamp | null;
    lastRemark?: string | null;
}

interface FollowUp {
    followUpTime: Timestamp;
    followUp: string;
    [key: string]: any;
}

interface FetchLeadsResponse {
    data: {
        leads: LeadData[];
        hasMore: boolean;
        lastDocument: {
            id: string;
            leadDate: Timestamp;
        } | null;
        count: number;
        // Optionally return first/last doc id for navigation
        firstDocId?: string | null;
        lastDocId?: string | null;
    }
}

// Helper to fetch a DocumentSnapshot by ID
async function getLeadDocSnapshot(db: Firestore, companyId: string, docId: string) {
    const docRef = doc(db, `companies/${companyId}/leads`, docId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap : null;
}

// function tofetchleads
// export async function fetchLeads(
//     db: Firestore,
//     user: User | null,
//     request: FetchLeadsRequest,
//     companyData?: { companyId: string | undefined },
// ): Promise<FetchLeadsResponse> {
//     try {
//         console.log("::::::::::::::::::::::::::::::::::::::::::::::::::::::");
//         console.log("request::::::::", request);
//         console.log("::::::::::::::::::::::::::::::::::::::::::::::::::::::");

//         // Check authentication
//         if (!user) {
//             throw new Error('Only authenticated users can fetch leads');
//         }

//         let userDoc: any;

//         if (request?.subAgentId) {
//             if (companyData?.companyId && request.subAgentId) {
//                 userDoc = await getDoc(doc(db, 'companies', companyData.companyId, 'subAgents', request.subAgentId));
//             } else {
//                 throw new Error('Company ID or Sub Agent ID is missing');
//             }
//         } else {
//             userDoc = await getDoc(doc(db, 'users', user.uid));
//         }

//         const userData = userDoc.data() as UserData;

//         if (!userData || !userData.companyId) {
//             throw new Error('No company associated with your account');
//         }

//         // Set default page size or use requested size
//         const pageSize = request.pageSize || 10;

//         // Determine which date field to use for ordering based on filters
//         const hasUpdatedAtFilter = request?.updatedAtStartDate || request?.updatedAtEndDate;
//         const orderByField = hasUpdatedAtFilter ? 'updatedAt' : 'createdAt';

//         console.log("Using orderBy field:", orderByField);

//         // Helper function to check if a filter is for "No [Filter]" option
//         const isNoFilterOption = (filterValue: string) => {
//             return filterValue === 'no-filter' || filterValue === 'null' || filterValue === 'empty';
//         };

//         // Build query constraints based on filters
//         const buildQueryConstraints = () => {
//             const constraints: QueryConstraint[] = [];

//             // If searchValue or phoneValue is present, only use those for filtering
//             if (request?.searchValue || request?.phoneValue) {
//                 if (request?.searchValue) {
//                     const upperCaseQuery = request.searchValue.trim().toUpperCase();
//                     console.log("upperCaseQuery", upperCaseQuery);
//                     constraints.push(
//                         where('name', '>=', upperCaseQuery),
//                         where('name', '<=', upperCaseQuery + '\uf8ff')
//                     );
//                 }

//                 if (request?.phoneValue) {
//                     const number = request.phoneValue.trim();
//                     constraints.push(
//                         where('number', '>=', number),
//                         where('number', '<=', number + '\uf8ff')
//                     );
//                 }

//                 if (request.isActive) {
//                     constraints.push(where('isActive', '==', request?.isActive ?? request.isActive));
//                 }
//             } else {
//                 // Only add other filters if there's no search or phone value

//                 if (request.isActive) {
//                     constraints.push(where('isActive', '==', request?.isActive ?? request.isActive));
//                 }

//                 if (request.activeFollowUpIds?.length === 1) {
//                     if (request.activeFollowUpIds[0] === 'false') {
//                         constraints.push(where('hasActiveFollowUp', '==', false));
//                     } else if (request.activeFollowUpIds[0] === 'true') {
//                         constraints.push(where('hasActiveFollowUp', '==', true));
//                     }
//                 }

//                 // Add filters conditionally - checking for "No Filter" options and regular filters
//                 if (request.sourceIds?.length === 1) {
//                     console.log("sourceIds", request.sourceIds[0]);

//                     if (isNoFilterOption(request.sourceIds[0])) {
//                         // For "No Source" - show leads with null, empty string, or "0" sourceId
//                         constraints.push(where('sourceId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('sourceId', '==', request.sourceIds[0]));
//                     }
//                 }

//                 if (request.stateIds?.length === 1) {
//                     console.log("stateIds", request.stateIds[0]);

//                     if (isNoFilterOption(request.stateIds[0])) {
//                         constraints.push(where('stateId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('stateId', '==', request.stateIds[0]));
//                     }
//                 }

//                 if (request.stageIds?.length === 1) {
//                     if (isNoFilterOption(request.stageIds[0])) {
//                         constraints.push(where('stageId', '==', null));
//                         constraints.push(where('stageId', 'in', ['', '0', 0]));
//                     } else {
//                         constraints.push(where('stageId', '==', request.stageIds[0]));
//                     }
//                 }

//                 if (request.statusIds?.length === 1) {
//                     if (isNoFilterOption(request.statusIds[0])) {
//                         constraints.push(where('statusId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('statusId', '==', request.statusIds[0]));
//                     }
//                 }

//                 if (request.admissionStatusIds?.length === 1) {
//                     if (isNoFilterOption(request.admissionStatusIds[0])) {
//                         constraints.push(where('admissionStatusId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('admissionStatusId', '==', request.admissionStatusIds[0]));
//                     }
//                 }

//                 if (request.welcomeCallStatusIds?.length === 1) {
//                     if (isNoFilterOption(request.welcomeCallStatusIds[0])) {
//                         constraints.push(where('welcomeCallByAdmissionTeamStatusId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('welcomeCallByAdmissionTeamStatusId', '==', request.welcomeCallStatusIds[0]));
//                     }
//                 }

//                 if (request.visaProcessStatusIds?.length === 1) {
//                     if (isNoFilterOption(request.visaProcessStatusIds[0])) {
//                         constraints.push(where('visaFileProcessingStatusId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('visaFileProcessingStatusId', '==', request.visaProcessStatusIds[0]));
//                     }
//                 }

//                 if (request.applicationStatusIds?.length === 1) {
//                     if (isNoFilterOption(request.applicationStatusIds[0])) {
//                         constraints.push(where('applicationMovedStatusId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('applicationMovedStatusId', '==', request.applicationStatusIds[0]));
//                     }
//                 }

//                 if (request.conditionalOfferLetterStatusIds?.length === 1) {
//                     if (isNoFilterOption(request.conditionalOfferLetterStatusIds[0])) {
//                         constraints.push(where('conditionalOfferLetterStatusId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('conditionalOfferLetterStatusId', '==', request.conditionalOfferLetterStatusIds[0]));
//                     }
//                 }

//                 if (request.unconditionalOfferLetterStatusIds?.length === 1) {
//                     if (isNoFilterOption(request.unconditionalOfferLetterStatusIds[0])) {
//                         constraints.push(where('unconditionalOfferLetterStatusId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('unconditionalOfferLetterStatusId', '==', request.unconditionalOfferLetterStatusIds[0]));
//                     }
//                 }

//                 if (request.feePaymentStatusIds?.length === 1) {
//                     if (isNoFilterOption(request.feePaymentStatusIds[0])) {
//                         constraints.push(where('feePaymentStatusId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('feePaymentStatusId', '==', request.feePaymentStatusIds[0]));
//                     }
//                 }

//                 if (request.fileSubmissionStatusIds?.length === 1) {
//                     if (isNoFilterOption(request.fileSubmissionStatusIds[0])) {
//                         constraints.push(where('fileSubmissionStatusId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('fileSubmissionStatusId', '==', request.fileSubmissionStatusIds[0]));
//                     }
//                 }

//                 if (request.campaignIds?.length === 1) {
//                     console.log("campaignIds", request.campaignIds[0]);
//                     if (isNoFilterOption(request.campaignIds[0])) {
//                         constraints.push(where('campaignId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('campaignId', '==', request.campaignIds[0]));
//                     }
//                 }

//                 if (request.preferredStreamIds?.length === 1) {
//                     if (isNoFilterOption(request.preferredStreamIds[0])) {
//                         constraints.push(where('preferredStreamId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('preferredStreamId', '==', request.preferredStreamIds[0]));
//                     }
//                 }

//                 if (request.destinationIds?.length === 1) {
//                     if (isNoFilterOption(request.destinationIds[0])) {
//                         constraints.push(where('destinationId', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('destinationId', '==', request.destinationIds[0]));
//                     }
//                 }

//                 if (request.intakeMonth?.length === 1) {
//                     console.log(">>>>>>>>", request.intakeMonth);
//                     if (isNoFilterOption(request.intakeMonth[0])) {
//                         constraints.push(where('intakeMonth', 'in', [null, '', '0', 0]));
//                     } else {
//                         constraints.push(where('intakeMonth', '==', request.intakeMonth[0]));
//                     }
//                 }

//                 if (request.intakeYear?.length === 1) {
//                     console.log(">>>>>>>>", request.intakeYear);
//                     const value = request.intakeYear[0];

//                     if (isNoFilterOption(value)) {
//                         constraints.push(where('intakeYear', 'in', [null, '', '0', 0]));
//                     } else {
//                         const numValue = Number(value);
//                         const strValue = String(value);
//                         // Use OR to match both number and string versions
//                         constraints.push(where('intakeYear', 'in', [numValue, strValue]));
//                     }
//                 }

//                 // Handle date filtering for tranffertAt field
//                 if (request?.tranffertAtStartDate && request?.tranffertAtEndDate) {
//                     constraints.push(
//                         where('transferredAt', '>=', request.tranffertAtStartDate),
//                         where('transferredAt', '<=', request.tranffertAtEndDate)
//                     );
//                 } else if (request?.tranffertAtStartDate) {
//                     constraints.push(where('transferredAt', '>=', request.tranffertAtStartDate));
//                 } else if (request?.tranffertAtEndDate) {
//                     constraints.push(where('transferredAt', '<=', request.tranffertAtEndDate));
//                 }

//                 // Handle date filtering for createdAt field
//                 if (request?.startDate && request?.endDate) {
//                     constraints.push(
//                         where('createdAt', '>=', request.startDate),
//                         where('createdAt', '<=', request.endDate)
//                     );
//                 } else if (request?.startDate) {
//                     constraints.push(where('createdAt', '>=', request.startDate));
//                 } else if (request?.endDate) {
//                     constraints.push(where('createdAt', '<=', request.endDate));
//                 }

//                 // Handle date filtering for updatedAt field
//                 if (request?.updatedAtStartDate && request?.updatedAtEndDate) {
//                     console.log("request?.updatedAtStartDate", request?.updatedAtStartDate);
//                     console.log("request?.updatedAtEndDate", request?.updatedAtEndDate);

//                     constraints.push(
//                         where('updatedAt', '>=', request.updatedAtStartDate),
//                         where('updatedAt', '<=', request.updatedAtEndDate)
//                     );
//                 } else if (request?.updatedAtStartDate) {
//                     constraints.push(where('updatedAt', '>=', request.updatedAtStartDate));
//                 } else if (request?.updatedAtEndDate) {
//                     constraints.push(where('updatedAt', '<=', request.updatedAtEndDate));
//                 }
//             }

//             if (request?.assignedTo) {
//                 console.log("1. assignedTo exists:", request.assignedTo);

//                 if (Array.isArray(request.assignedTo)) {
//                     console.log("2. is array with length:", request.assignedTo.length);

//                     if (request.assignedTo.length === 1) {
//                         console.log("3a. Single item case:", request.assignedTo[0]);
//                         if (isNoFilterOption(request.assignedTo[0])) {
//                             constraints.push(where('assignedTo', 'in', [null, '', '0', 0]));
//                         } else {
//                             constraints.push(where('assignedTo', '==', request.assignedTo[0]));
//                         }
//                     } else if (request.assignedTo.length > 1) {
//                         console.log("3b. Multiple items case:", request.assignedTo);
//                         constraints.push(where('assignedTo', 'in', request.assignedTo));
//                     }
//                 }
//             }

//             if (request?.subAgent) {
//                 console.log("1. subAgent exists:", request.subAgent);

//                 if (Array.isArray(request.subAgent)) {
//                     console.log("2. is array with length:", request.subAgent.length);

//                     if (request.subAgent.length === 1) {
//                         console.log("3a. Single item case:", request.subAgent[0]);
//                         if (isNoFilterOption(request.subAgent[0])) {
//                             constraints.push(where('subAgentId', 'in', [null, '', '0', 0]));
//                         } else {
//                             constraints.push(where('subAgentId', '==', request.subAgent[0]));
//                         }
//                     } else if (request.subAgent.length > 1) {
//                         console.log("3b. Multiple items case:", request.subAgent);
//                         constraints.push(where('subAgentId', 'in', request.subAgent));
//                     }
//                 }
//             }

//             if (request?.subAgentId) {
//                 console.log("1. subAgentId exists:", request.subAgentId);
//                 if (isNoFilterOption(request.subAgentId)) {
//                     constraints.push(where('subAgentId', 'in', [null, '', '0', 0]));
//                 } else {
//                     constraints.push(where('subAgentId', '==', request.subAgentId));
//                 }
//             }

//             return constraints;
//         };

//         // Build base query constraints
//         const baseConstraints = buildQueryConstraints();

//         // Add the appropriate ordering constraint
//         const queryConstraintsWithOrder = [...baseConstraints, orderBy(orderByField, 'desc')];

//         // Pagination navigation logic  
//         let leadsQuery;
//         let currentPage = 1;

//         if (request.direction === 'first' || !request.direction) {
//             // First page
//             currentPage = 1;
//             leadsQuery = query(
//                 collection(db, `companies/${userData.companyId}/leads`),
//                 ...queryConstraintsWithOrder,
//                 limit(pageSize)
//             );
//         } else if (request.direction === 'next') {
//             // Next page - FIXED: Use startAfter with the document snapshot, not just the field value
//             currentPage = 1;
//             if (request.cursorId) {
//                 console.log("cursor", request.cursorId);
//                 const lastDocSnapshot = await getDoc(doc(db, `companies/${userData.companyId}/leads`, request.cursorId));
//                 if (lastDocSnapshot.exists()) {
//                     // Use the document snapshot directly with startAfter
//                     leadsQuery = query(
//                         collection(db, `companies/${userData.companyId}/leads`),
//                         ...queryConstraintsWithOrder,
//                         startAfter(lastDocSnapshot), // Use the document snapshot, not just the field value
//                         limit(pageSize)
//                     );
//                     console.log("leadsQuery with startAfter", leadsQuery);
//                 } else {
//                     // Fallback to first page if cursor document doesn't exist
//                     leadsQuery = query(
//                         collection(db, `companies/${userData.companyId}/leads`),
//                         ...queryConstraintsWithOrder,
//                         limit(pageSize)
//                     );
//                 }
//             } else {
//                 // Fallback to first page if no cursor
//                 leadsQuery = query(
//                     collection(db, `companies/${userData.companyId}/leads`),
//                     ...queryConstraintsWithOrder,
//                     limit(pageSize)
//                 );
//             }
//         } else if (request.direction === 'prev') {
//             // Previous page - FIXED: Use endBefore with the document snapshot
//             currentPage = 1;
//             if (request.cursorId) {
//                 const firstDocSnapshot = await getDoc(doc(db, `companies/${userData.companyId}/leads`, request.cursorId));
//                 if (firstDocSnapshot.exists()) {
//                     // Use the document snapshot directly with endBefore
//                     leadsQuery = query(
//                         collection(db, `companies/${userData.companyId}/leads`),
//                         ...queryConstraintsWithOrder,
//                         endBefore(firstDocSnapshot), // Use the document snapshot, not just the field value
//                         limitToLast(pageSize)
//                     );
//                 } else {
//                     // Fallback to first page if cursor document doesn't exist
//                     leadsQuery = query(
//                         collection(db, `companies/${userData.companyId}/leads`),
//                         ...queryConstraintsWithOrder,
//                         limit(pageSize)
//                     );
//                 }
//             } else {
//                 // Fallback to first page if no cursor
//                 leadsQuery = query(
//                     collection(db, `companies/${userData.companyId}/leads`),
//                     ...queryConstraintsWithOrder,
//                     limit(pageSize)
//                 );
//             }
//         } else {
//             // Fallback to first page
//             leadsQuery = query(
//                 collection(db, `companies/${userData.companyId}/leads`),
//                 ...queryConstraintsWithOrder,
//                 limit(pageSize)
//             );
//         }

//         console.log("leadsQuery", leadsQuery);

//         // Fetch leads with the constructed query
//         const leadsSnapshot = await getDocs(leadsQuery);
//         let leads = leadsSnapshot.docs.map((doc) => {
//             const leadData = doc.data() as LeadDocumentData;
//             return {
//                 ...leadData,
//                 id: doc.id,
//                 followUp: null
//             } as LeadData;
//         });

//         // Check if there are more results by trying to fetch one more document
//         const hasMoreQuery = query(
//             collection(db, `companies/${userData.companyId}/leads`),
//             ...queryConstraintsWithOrder,
//             ...(leads.length > 0 ? [startAfter(leadsSnapshot.docs[leadsSnapshot.docs.length - 1])] : []),
//             limit(1)
//         );

//         const hasMoreSnapshot = await getDocs(hasMoreQuery);
//         const hasMore = hasMoreSnapshot.docs.length > 0;

//         // Get total count using the base constraints (without order, limit, or pagination)
//         const countQueryConstraints = baseConstraints;

//         const totalLeadsQuery = query(
//             collection(db, `companies/${userData.companyId}/leads`),
//             ...countQueryConstraints
//         );

//         const totalLeadsSnapshot = await getCountFromServer(totalLeadsQuery);
//         const totalLeadsCount = totalLeadsSnapshot.data().count;

//         const firstDoc = leads[0] || null;
//         const lastDoc = leads[leads.length - 1] || null;

//         console.log("leads", leads);

//         return {
//             data: {
//                 leads,
//                 hasMore,
//                 lastDocument: lastDoc ? {
//                     id: lastDoc.id,
//                     leadDate: lastDoc[orderByField] // Use the appropriate date field
//                 } : null,
//                 count: totalLeadsCount,
//                 firstDocId: firstDoc ? firstDoc.id : null,
//                 lastDocId: lastDoc ? lastDoc.id : null
//             }
//         };

//     } catch (error) {
//         const errorMessage = error instanceof Error ? error.message : 'Error fetching leads';
//         console.error('Error in fetchLeads:', error);
//         throw new Error(errorMessage);
//     }
// }


// 


export async function fetchLeads(
    db: Firestore,
    user: User | null,
    request: FetchLeadsRequest,
    companyData?: { companyId: string | undefined },
): Promise<FetchLeadsResponse> {
    try {
        console.log("::::::::::::::::::::::::::::::::::::::::::::::::::::::");
        console.log("request::::::::", request);
        console.log("::::::::::::::::::::::::::::::::::::::::::::::::::::::");

        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch leads');
        }

        let userDoc: any;

        if (request?.subAgentId) {
            if (companyData?.companyId && request.subAgentId) {
                userDoc = await getDoc(doc(db, 'companies', companyData.companyId, 'subAgents', request.subAgentId));
            } else {
                throw new Error('Company ID or Sub Agent ID is missing');
            }
        } else {
            userDoc = await getDoc(doc(db, 'users', user.uid));
        }

        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Set default page size or use requested size
        const pageSize = Math.max(1, request.pageSize || 10);

        // Determine which date field to use for ordering
        if (request?.orderByField) {
            orderBy(request?.orderByField, 'desc')
            console.log("Using orderBy field:", request?.orderByField);
        }
        // Helper function to check if a filter is for "No [Filter]" option
        const isNoFilterOption = (filterValue: string) => {
            return filterValue === 'no-filter' || filterValue === 'null' || filterValue === 'empty';
        };

        // Build query constraints based on filters
        const buildQueryConstraints = () => {
            const constraints: QueryConstraint[] = [];
            // const filterConditions: QueryCompositeFilterConstraint[] = [];

            if (request?.searchValue || request?.phoneValue) {
                if (request?.searchValue) {
                    const upperCaseQuery = request.searchValue.trim().toUpperCase();
                    console.log("upperCaseQuery", upperCaseQuery);
                    constraints.push(
                        where('name', '>=', upperCaseQuery),
                        where('name', '<=', upperCaseQuery + '\uf8ff')
                    );
                }

                if (request?.phoneValue) {
                    const number = request.phoneValue.trim();
                    constraints.push(
                        where('number', '>=', number),
                        where('number', '<=', number + '\uf8ff')
                    );
                }

                if (request.isActive) {
                    constraints.push(where('isActive', '==', request?.isActive ?? request.isActive));
                }
            } else {
                if (request.isActive) {
                    constraints.push(where('isActive', '==', request?.isActive ?? request.isActive));
                }

                if (request.activeFollowUpIds?.length === 1) {
                    if (isNoFilterOption(request.activeFollowUpIds[0])) {
                        // For "No Active Follow Up" - show leads with hasActiveFollowUp as false
                        constraints.push(where('hasActiveFollowUp', '==', null));
                    } else {

                        if (request.activeFollowUpIds[0] === 'false') {
                            constraints.push(where('hasActiveFollowUp', '==', false));

                        } else if (request.activeFollowUpIds[0] === 'true') {
                            constraints.push(where('hasActiveFollowUp', '==', true));
                        }
                    }
                }

                // Add filters conditionally - checking for "No Filter" options and regular filters
                if (request.sourceIds && request.sourceIds.length > 0) {
                    console.log("sourceIds", request.sourceIds[0]);

                    if (isNoFilterOption(request.sourceIds[0])) {
                        // For "No Source" - show leads with null, empty string, or "0" sourceId
                        constraints.push(where('sourceId', '==', null));
                    } else {
                        console.log("sourceIds", request.sourceIds);
                        constraints.push(where('sourceId', 'in', request.sourceIds));
                    }
                }

                if (request.stateIds && request.stateIds.length > 0) {
                    console.log("stateIds", request.stateIds[0]);

                    if (isNoFilterOption(request.stateIds[0])) {
                        constraints.push(where('stateId', '==', null));
                    } else {
                        constraints.push(where('stateId', 'in', request.stateIds));
                    }
                }

                if (request.stageIds && request.stageIds.length > 0) {
                    if (isNoFilterOption(request.stageIds[0])) {
                        constraints.push(where('stageId', '==', null));
                    } else {
                        constraints.push(where('stageId', 'in', request.stageIds));
                    }
                }

                if (request.statusIds && request.statusIds.length > 0) {
                    if (isNoFilterOption(request.statusIds[0])) {
                        constraints.push(where('statusId', '==', null));
                    } else {
                        constraints.push(where('statusId', 'in', request.statusIds));
                    }
                }

                if (request.admissionStatusIds && request.admissionStatusIds.length > 0) {
                    if (isNoFilterOption(request.admissionStatusIds[0])) {
                        constraints.push(where('admissionStatusId', '==', null));
                    } else {
                        constraints.push(where('admissionStatusId', 'in', request.admissionStatusIds));
                    }
                }

                if (request.welcomeCallStatusIds && request.welcomeCallStatusIds.length > 0) {
                    if (isNoFilterOption(request.welcomeCallStatusIds[0])) {
                        constraints.push(where('welcomeCallByAdmissionTeamStatusId', '==', null));
                    } else {
                        constraints.push(where('welcomeCallByAdmissionTeamStatusId', 'in', request.welcomeCallStatusIds));
                    }
                }

                if (request.visaProcessStatusIds && request.visaProcessStatusIds.length > 0) {
                    if (isNoFilterOption(request.visaProcessStatusIds[0])) {
                        constraints.push(where('visaFileProcessingStatusId', '==', null));
                    } else {
                        constraints.push(where('visaFileProcessingStatusId', 'in', request.visaProcessStatusIds));
                    }
                }

                if (request.applicationStatusIds && request.applicationStatusIds.length > 0) {
                    if (isNoFilterOption(request.applicationStatusIds[0])) {
                        constraints.push(where('applicationMovedStatusId', '==', null));
                    } else {
                        constraints.push(where('applicationMovedStatusId', 'in', request.applicationStatusIds));
                    }
                }

                if (request.conditionalOfferLetterStatusIds && request.conditionalOfferLetterStatusIds.length > 0) {
                    if (isNoFilterOption(request.conditionalOfferLetterStatusIds[0])) {
                        constraints.push(where('conditionalOfferLetterStatusId', '==', null));
                    } else {
                        constraints.push(where('conditionalOfferLetterStatusId', 'in', request.conditionalOfferLetterStatusIds));
                    }
                }

                if (request.unconditionalOfferLetterStatusIds && request.unconditionalOfferLetterStatusIds.length > 0) {
                    if (isNoFilterOption(request.unconditionalOfferLetterStatusIds[0])) {
                        constraints.push(where('unconditionalOfferLetterStatusId', '==', null));
                    } else {
                        constraints.push(where('unconditionalOfferLetterStatusId', 'in', request.unconditionalOfferLetterStatusIds));
                    }
                }

                if (request.feePaymentStatusIds && request.feePaymentStatusIds.length > 0) {
                    if (isNoFilterOption(request.feePaymentStatusIds[0])) {
                        constraints.push(where('feePaymentStatusId', '==', null));
                    } else {
                        constraints.push(where('feePaymentStatusId', 'in', request.feePaymentStatusIds));
                    }
                }

                if (request.fileSubmissionStatusIds && request.fileSubmissionStatusIds.length > 0) {
                    if (isNoFilterOption(request.fileSubmissionStatusIds[0])) {
                        constraints.push(where('fileSubmissionStatusId', '==', null));
                    } else {
                        constraints.push(where('fileSubmissionStatusId', 'in', request.fileSubmissionStatusIds));
                    }
                }

                if (request.campaignIds && request.campaignIds.length > 0) {
                    console.log("campaignIds", request.campaignIds[0]);
                    if (isNoFilterOption(request.campaignIds[0])) {
                        constraints.push(where('campaignId', '==', null));
                    } else {
                        constraints.push(where('campaignId', 'in', request.campaignIds));
                    }
                }

                if (request.preferredStreamIds && request.preferredStreamIds.length > 0) {
                    if (isNoFilterOption(request.preferredStreamIds[0])) {
                        constraints.push(where('preferredStreamId', '==', null));
                    } else {
                        constraints.push(where('preferredStreamId', 'in', request.preferredStreamIds));
                    }
                }

                // super Agent

                if (request.superAgentId && request.superAgentId.length > 0) {
                    console.log("request.superAgentId", request.superAgentId);

                    if (isNoFilterOption(request.superAgentId[0])) {
                        constraints.push(where('superAgents', '==', null));
                    } else {
                        constraints.push(where('superAgents', 'in', request.superAgentId));
                    }
                }

                // lead Admission SubStatus
                if (request.admissionSubStatus && request.admissionSubStatus.length > 0) {
                    if (isNoFilterOption(request.admissionSubStatus[0])) {
                        constraints.push(where('subAdmissionStatusId', '==', null));
                    } else {
                        constraints.push(where('subAdmissionStatusId', 'in', request.admissionSubStatus));
                    }
                }

                // lead Sub Sources
                if (request.leadSubSources && request.leadSubSources.length > 0) {
                    console.log("subsorce", request.leadSubSources);

                    if (isNoFilterOption(request.leadSubSources[0])) {
                        constraints.push(where('subSourceId', '==', null));
                    } else {
                        constraints.push(where('subSourceId', 'in', request.leadSubSources));
                    }
                }

                //  leadSubStages 
                if (request.leadSubStages && request.leadSubStages.length > 0) {
                    if (isNoFilterOption(request.leadSubStages[0])) {
                        constraints.push(where('subStageId', '==', null));
                    } else {
                        constraints.push(where('subStageId', 'in', request.leadSubStages));
                    }
                }

                //  leadSubStatus
                if (request.leadSubStatuses && request.leadSubStatuses.length > 0) {
                    if (isNoFilterOption(request.leadSubStatuses[0])) {
                        constraints.push(where('subStatusId', '==', null));
                    } else {
                        constraints.push(where('subStatusId', 'in', request.leadSubStatuses));
                    }
                }

                if (request.destinationIds && request.destinationIds.length > 0) {
                    if (isNoFilterOption(request.destinationIds[0])) {
                        constraints.push(where('destinationId', '==', null));
                    } else {
                        constraints.push(where('destinationId', 'in', request.destinationIds));
                    }
                }

                if (request.intakeMonth?.length === 1) {
                    console.log(">>>>>>>>", request.intakeMonth);
                    if (isNoFilterOption(request.intakeMonth[0])) {
                        // constraints.push(where('intakeMonth', 'in', [null, '', '0', 0]));
                    } else {
                        constraints.push(where('intakeMonth', '==', request.intakeMonth[0]));
                    }
                }

                if (request.intakeYear?.length === 1) {
                    console.log(">>>>>>>>", request.intakeYear);
                    const value = request.intakeYear[0];

                    if (isNoFilterOption(value)) {
                        // constraints.push(where('intakeYear', 'in', [null, '', '0', 0]));
                    } else {
                        const numValue = Number(value);
                        const strValue = String(value);
                        // Use OR to match both number and string versions
                        constraints.push(where('intakeYear', 'in', [numValue, strValue]));
                    }
                }

                // Handle date filtering for tranffertAt field
                if (request?.tranffertAtStartDate && request?.tranffertAtEndDate) {
                    constraints.push(
                        where('transferredAt', '>=', request.tranffertAtStartDate),
                        where('transferredAt', '<=', request.tranffertAtEndDate)
                    );
                } else if (request?.tranffertAtStartDate) {
                    constraints.push(where('transferredAt', '>=', request.tranffertAtStartDate));
                } else if (request?.tranffertAtEndDate) {
                    constraints.push(where('transferredAt', '<=', request.tranffertAtEndDate));
                }

                // Handle date filtering for createdAt field
                if (request?.startDate && request?.endDate) {
                    constraints.push(
                        where('createdAt', '>=', request.startDate),
                        where('createdAt', '<=', request.endDate)
                    );
                } else if (request?.startDate) {
                    constraints.push(where('createdAt', '>=', request.startDate));
                } else if (request?.endDate) {
                    constraints.push(where('createdAt', '<=', request.endDate));
                }

                // Handle date filtering for updatedAt field
                if (request?.updatedAtStartDate && request?.updatedAtEndDate) {
                    console.log("request?.updatedAtStartDate", request?.updatedAtStartDate);
                    console.log("request?.updatedAtEndDate", request?.updatedAtEndDate);

                    constraints.push(
                        where('updatedAt', '>=', request.updatedAtStartDate),
                        where('updatedAt', '<=', request.updatedAtEndDate)
                    );
                } else if (request?.updatedAtStartDate) {
                    constraints.push(where('updatedAt', '>=', request.updatedAtStartDate));
                } else if (request?.updatedAtEndDate) {
                    constraints.push(where('updatedAt', '<=', request.updatedAtEndDate));
                }

                // hnadle date filtering for followUpDate field
                if (request?.followUpAtStartDate && request?.followUpAtEndDate) {
                    console.log("request?.followUpAtStartDate", request?.followUpAtStartDate);
                    console.log("request?.followUpAtEndDate", request?.followUpAtEndDate);
                    constraints.push(
                        where('activeFollowUpTime', '>=', request.followUpAtStartDate),
                        where('activeFollowUpTime', '<=', request.followUpAtEndDate)
                    );
                } else if (request?.followUpAtStartDate) {
                    constraints.push(where('activeFollowUpTime', '>=', request.followUpAtStartDate));
                } else if (request?.followUpAtEndDate) {
                    constraints.push(where('activeFollowUpTime', '<=', request.followUpAtEndDate));
                }
            }

            if (request?.assignedTo) {
                console.log("1. assignedTo exists:", request.assignedTo);

                if (Array.isArray(request.assignedTo)) {
                    console.log("2. is array with length:", request.assignedTo.length);

                    if (request.assignedTo.length === 1) {
                        console.log("3a. Single item case:", request.assignedTo[0]);
                        if (isNoFilterOption(request.assignedTo[0])) {
                            constraints.push(where('assignedTo', '==', null));
                        } else {
                            constraints.push(where('assignedTo', '==', request.assignedTo[0]));
                        }
                    } else if (request.assignedTo.length > 1) {
                        console.log("3b. Multiple items case:", request.assignedTo);
                        constraints.push(where('assignedTo', 'in', request.assignedTo));
                    }
                }
            }

            if (request?.subAgent) {
                console.log("1. subAgent exists:", request.subAgent);

                if (Array.isArray(request.subAgent)) {
                    console.log("2. is array with length:", request.subAgent.length);

                    if (request.subAgent.length === 1) {
                        console.log("3a. Single item case:", request.subAgent[0]);
                        if (isNoFilterOption(request.subAgent[0])) {
                            constraints.push(where('subAgentId', '==', null));
                        } else {
                            constraints.push(where('subAgentId', '==', request.subAgent[0]));
                        }
                    } else if (request.subAgent.length > 1) {
                        console.log("3b. Multiple items case:", request.subAgent);
                        constraints.push(where('subAgentId', 'in', request.subAgent));
                    }
                }
            }

            if (request?.subAgentId) {
                console.log("1. subAgentId exists:", request.subAgentId);
                if (isNoFilterOption(request.subAgentId)) {
                    constraints.push(where('subAgentId', '==', null));
                } else {
                    constraints.push(where('subAgentId', '==', request.subAgentId));
                }
            }

            return constraints;
        };

        // Build base query constraints
        const baseConstraints = buildQueryConstraints();

        // Add the appropriate ordering constraint with sort direction
        const sortDir = request?.sortDirection || 'desc'; // Default to 'desc' if not specified
        const queryConstraintsWithOrder = [...baseConstraints, orderBy(request?.orderByField, sortDir)];

        // Pagination navigation logic  
        let leadsQuery;
        let currentPage = 1;

        if (request.direction === 'first' || !request.direction) {
            // First page
            currentPage = 1;
            leadsQuery = query(
                collection(db, `companies/${userData.companyId}/leads`),
                ...queryConstraintsWithOrder,
                limit(pageSize)
            );
        } else if (request.direction === 'next') {
            // Next page - FIXED: Use startAfter with the document snapshot, not just the field value
            currentPage = 1;
            if (request.cursorId) {
                console.log("cursor", request.cursorId);
                const lastDocSnapshot = await getDoc(doc(db, `companies/${userData.companyId}/leads`, request.cursorId));
                if (lastDocSnapshot.exists()) {
                    // Use the document snapshot directly with startAfter
                    leadsQuery = query(
                        collection(db, `companies/${userData.companyId}/leads`),
                        ...queryConstraintsWithOrder,
                        startAfter(lastDocSnapshot), // Use the document snapshot, not just the field value
                        limit(pageSize)
                    );
                    console.log("leadsQuery with startAfter", leadsQuery);
                } else {
                    // Fallback to first page if cursor document doesn't exist
                    leadsQuery = query(
                        collection(db, `companies/${userData.companyId}/leads`),
                        ...queryConstraintsWithOrder,
                        limit(pageSize)
                    );
                }
            } else {
                // Fallback to first page if no cursor
                leadsQuery = query(
                    collection(db, `companies/${userData.companyId}/leads`),
                    ...queryConstraintsWithOrder,
                    limit(pageSize)
                );
            }
        } else if (request.direction === 'prev') {
            // Previous page - FIXED: Use endBefore with the document snapshot
            currentPage = 1;
            if (request.cursorId) {
                const firstDocSnapshot = await getDoc(doc(db, `companies/${userData.companyId}/leads`, request.cursorId));
                if (firstDocSnapshot.exists()) {
                    // Use the document snapshot directly with endBefore
                    leadsQuery = query(
                        collection(db, `companies/${userData.companyId}/leads`),
                        ...queryConstraintsWithOrder,
                        endBefore(firstDocSnapshot), // Use the document snapshot, not just the field value
                        limitToLast(pageSize)
                    );
                } else {
                    // Fallback to first page if cursor document doesn't exist
                    leadsQuery = query(
                        collection(db, `companies/${userData.companyId}/leads`),
                        ...queryConstraintsWithOrder,
                        limit(pageSize)
                    );
                }
            } else {
                // Fallback to first page if no cursor
                leadsQuery = query(
                    collection(db, `companies/${userData.companyId}/leads`),
                    ...queryConstraintsWithOrder,
                    limit(pageSize)
                );
            }
        } else {
            // Fallback to first page
            leadsQuery = query(
                collection(db, `companies/${userData.companyId}/leads`),
                ...queryConstraintsWithOrder,
                limit(pageSize)
            );
        }

        console.log("leadsQuery", leadsQuery);

        // Fetch leads with the constructed query
        const leadsSnapshot = await getDocs(leadsQuery);
        let leads = leadsSnapshot.docs.map((doc) => {
            const leadData = doc.data() as LeadDocumentData;
            return {
                ...leadData,
                id: doc.id,
                followUp: null
            } as LeadData;
        });

        // Fetch latest remark for each lead
        await Promise.all(leads.map(async (lead) => {
            try {
                const remarksRef = collection(db, `companies/${userData.companyId}/leads/${lead.id}/remarks`);
                const remarksQuery = query(remarksRef, orderBy('createdAt', 'desc'), limit(1));
                const remarksSnapshot = await getDocs(remarksQuery);

                if (!remarksSnapshot.empty) {
                    const remarkData = remarksSnapshot.docs[0].data() as LeadRemark;
                    lead.lastRemark = remarkData.remark || null;
                } else {
                    lead.lastRemark = null;
                }
            } catch (error) {
                console.warn('Error fetching latest remark for lead', lead.id, ':', error);
                lead.lastRemark = null;
            }
        }));

        // Log the fetched leads
        console.log("leadsSnapshot.docs", leadsSnapshot.docs);
        console.log("leads", leads);



        // Check if there are more results by trying to fetch one more document
        const hasMoreQuery = query(
            collection(db, `companies/${userData.companyId}/leads`),
            ...queryConstraintsWithOrder,
            ...(leads.length > 0 ? [startAfter(leadsSnapshot.docs[leadsSnapshot.docs.length - 1])] : []),
            limit(1)
        );

        const hasMoreSnapshot = await getDocs(hasMoreQuery);
        const hasMore = hasMoreSnapshot.docs.length > 0;

        console.log("hasMoreQuery", hasMoreQuery);
        console.log("hasMoreSnapshot.docs", hasMoreSnapshot.docs);
        console.log("hasMore", hasMore);


        // Get total count using the base constraints (without order, limit, or pagination)
        const countQueryConstraints = baseConstraints;

        const totalLeadsQuery = query(
            collection(db, `companies/${userData.companyId}/leads`),
            ...countQueryConstraints
        );

        const totalLeadsSnapshot = await getCountFromServer(totalLeadsQuery);
        const totalLeadsCount = totalLeadsSnapshot.data().count;

        const firstDoc = leads[0] || null;
        const lastDoc = leads[leads.length - 1] || null;

        // /////////////////////////////////////
        console.log("firstDoc", firstDoc);
        console.log("lastDoc", lastDoc);
        // /////////////////////////////////////



        console.log(">>>>>>>>>>",);


        // sort leads by transferredAt field
        // leads.sort((a, b) => {
        //     const dateA = a.transferredAt ? a.transferredAt.toMillis() : 0;
        //     const dateB = b.transferredAt ? b.transferredAt.toMillis() : 0;
        //     return dateB - dateA; // Sort in descending order
        // });

        console.log("leads", leads);

        return {
            data: {
                leads,
                hasMore,
                lastDocument: lastDoc ? {
                    id: lastDoc.id,
                    leadDate: lastDoc['createdAt'] // Use the appropriate date field
                } : null,
                count: totalLeadsCount,
                firstDocId: firstDoc ? firstDoc.id : null,
                lastDocId: lastDoc ? lastDoc.id : null
            }
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error fetching leads';
        console.error('Error in fetchLeads:', error);
        throw new Error(errorMessage);
    }
}



async function getUserDocument(
    db: Firestore,
    user: User,
    request: FetchLeadsRequest,
    companyData?: { companyId: string | undefined }
) {
    if (request?.subAgentId) {
        if (!companyData?.companyId || !request.subAgentId) {
            throw new Error('Company ID or Sub Agent ID is missing');
        }
        return getDoc(doc(db, 'companies', companyData.companyId, 'subAgents', request.subAgentId));
    }
    return getDoc(doc(db, 'users', user.uid));
}

function buildQueryConstraints(request: FetchLeadsRequest): QueryConstraint[] {
    const constraints: QueryConstraint[] = [];
    const isNoFilterOption = (value: string) => ['no-filter', 'null', 'empty'].includes(value);

    // Search filters
    if (request?.searchValue) {
        const upperCaseQuery = request.searchValue.trim().toUpperCase();
        constraints.push(
            where('name', '>=', upperCaseQuery),
            where('name', '<=', upperCaseQuery + '\uf8ff')
        );
    }

    if (request?.phoneValue) {
        const number = request.phoneValue.trim();
        constraints.push(
            where('number', '>=', number),
            where('number', '<=', number + '\uf8ff')
        );
    }

    // Active status filter
    if (request.isActive !== undefined) {
        constraints.push(where('isActive', '==', request.isActive));
    }

    // Filter builders for different types
    const buildSingleValueFilter = (field: string, values?: string[]) => {
        if (values?.length === 1) {
            constraints.push(where(field, '==', isNoFilterOption(values[0]) ? null : values[0]));
        }
    };

    const buildArrayFilter = (field: string, values?: string[]) => {
        if (!values) return;

        if (values.length === 1) {
            constraints.push(where(field, '==', isNoFilterOption(values[0]) ? null : values[0]));
        } else if (values.length > 1) {
            constraints.push(where(field, 'in', values));
        }
    };

    // Apply filters using the builders
    buildSingleValueFilter('hasActiveFollowUp', request.activeFollowUpIds);
    buildSingleValueFilter('sourceId', request.sourceIds);
    buildSingleValueFilter('stateId', request.stateIds);
    buildSingleValueFilter('stageId', request.stageIds);
    buildSingleValueFilter('statusId', request.statusIds);
    buildSingleValueFilter('admissionStatusId', request.admissionStatusIds);
    buildSingleValueFilter('welcomeCallByAdmissionTeamStatusId', request.welcomeCallStatusIds);
    buildSingleValueFilter('visaFileProcessingStatusId', request.visaProcessStatusIds);
    buildSingleValueFilter('applicationMovedStatusId', request.applicationStatusIds);
    buildSingleValueFilter('conditionalOfferLetterStatusId', request.conditionalOfferLetterStatusIds);
    buildSingleValueFilter('unconditionalOfferLetterStatusId', request.unconditionalOfferLetterStatusIds);
    buildSingleValueFilter('feePaymentStatusId', request.feePaymentStatusIds);
    buildSingleValueFilter('fileSubmissionStatusId', request.fileSubmissionStatusIds);
    buildSingleValueFilter('campaignId', request.campaignIds);
    buildSingleValueFilter('preferredStreamId', request.preferredStreamIds);
    buildSingleValueFilter('superAgents', request.superAgentId);
    buildSingleValueFilter('subAdmissionStatusId', request.admissionSubStatus);
    buildSingleValueFilter('subSourceId', request.leadSubSources);
    buildSingleValueFilter('subStageId', request.leadSubStages);
    buildSingleValueFilter('subStatusId', request.leadSubStatuses);
    buildSingleValueFilter('destinationId', request.destinationIds);
    buildSingleValueFilter('intakeMonth', request.intakeMonth);

    // Special handling for intakeYear
    if (request.intakeYear?.length === 1 && !isNoFilterOption(request.intakeYear[0])) {
        const value = request.intakeYear[0];
        constraints.push(where('intakeYear', 'in', [Number(value), String(value)]));
    }

    // Date range filters
    const buildDateRangeFilter = (field: string, startDate?: any, endDate?: any) => {
        if (startDate && endDate) {
            constraints.push(
                where(field, '>=', startDate),
                where(field, '<=', endDate)
            );
        } else if (startDate) {
            constraints.push(where(field, '>=', startDate));
        } else if (endDate) {
            constraints.push(where(field, '<=', endDate));
        }
    };

    buildDateRangeFilter('transferredAt', request.tranffertAtStartDate, request.tranffertAtEndDate);
    buildDateRangeFilter('createdAt', request.startDate, request.endDate);
    buildDateRangeFilter('updatedAt', request.updatedAtStartDate, request.updatedAtEndDate);

    // Array filters
    buildArrayFilter('assignedTo', request.assignedTo ?? undefined);
    buildArrayFilter('subAgentId', request.subAgent ?? undefined);

    // Single value filters
    if (request.subAgentId) {
        constraints.push(where('subAgentId', '==', isNoFilterOption(request.subAgentId) ? null : request.subAgentId));
    }

    return constraints;
}

async function executePaginatedQuery(
    db: Firestore,
    companyId: string,
    queryConstraints: QueryConstraint[],
    request: FetchLeadsRequest,
    pageSize: number
) {
    const leadsCollection = collection(db, `companies/${companyId}/leads`);
    let leadsQuery;
    let currentPage = 1;

    if (!request.direction || request.direction === 'first') {
        // First page
        leadsQuery = query(leadsCollection, ...queryConstraints, limit(pageSize));
    } else if (request.direction === 'next' && request.cursorId) {
        // Next page
        const lastDocSnapshot = await getDoc(doc(db, `companies/${companyId}/leads`, request.cursorId));
        if (lastDocSnapshot.exists()) {
            leadsQuery = query(
                leadsCollection,
                ...queryConstraints,
                startAfter(lastDocSnapshot),
                limit(pageSize)
            );
        } else {
            leadsQuery = query(leadsCollection, ...queryConstraints, limit(pageSize));
        }
    } else if (request.direction === 'prev' && request.cursorId) {
        // Previous page
        const firstDocSnapshot = await getDoc(doc(db, `companies/${companyId}/leads`, request.cursorId));
        if (firstDocSnapshot.exists()) {
            leadsQuery = query(
                leadsCollection,
                ...queryConstraints,
                endBefore(firstDocSnapshot),
                limitToLast(pageSize)
            );
        } else {
            leadsQuery = query(leadsCollection, ...queryConstraints, limit(pageSize));
        }
    } else {
        // Fallback to first page
        leadsQuery = query(leadsCollection, ...queryConstraints, limit(pageSize));
    }

    const leadsSnapshot = await getDocs(leadsQuery);
    return { leadsSnapshot, currentPage };
}

function processLeadsSnapshot(leadsSnapshot: QuerySnapshot<DocumentData>) {
    return leadsSnapshot.docs.map((doc) => {
        const leadData = doc.data() as LeadDocumentData;
        return {
            ...leadData,
            id: doc.id,
            followUp: null
        } as LeadData;
    });
}

async function checkHasMoreLeads(
    db: Firestore,
    companyId: string,
    queryConstraints: QueryConstraint[],
    leadsSnapshot: QuerySnapshot<DocumentData>,
    pageSize: number
) {
    if (leadsSnapshot.docs.length === 0) return false;

    const hasMoreQuery = query(
        collection(db, `companies/${companyId}/leads`),
        ...queryConstraints,
        startAfter(leadsSnapshot.docs[leadsSnapshot.docs.length - 1]),
        limit(1)
    );

    const hasMoreSnapshot = await getDocs(hasMoreQuery);
    return hasMoreSnapshot.docs.length > 0;
}

async function getTotalLeadsCount(
    db: Firestore,
    companyId: string,
    baseConstraints: QueryConstraint[]
) {
    const totalLeadsQuery = query(
        collection(db, `companies/${companyId}/leads`),
        ...baseConstraints
    );

    const totalLeadsSnapshot = await getCountFromServer(totalLeadsQuery);
    return totalLeadsSnapshot.data().count;
}

function prepareResponse(
    leads: LeadData[],
    hasMore: boolean,
    totalLeadsCount: number
): FetchLeadsResponse {
    const firstDoc = leads[0] || null;
    const lastDoc = leads[leads.length - 1] || null;

    return {
        data: {
            leads,
            hasMore,
            lastDocument: lastDoc ? {
                id: lastDoc.id,
                leadDate: lastDoc['createdAt']
            } : null,
            count: totalLeadsCount,
            firstDocId: firstDoc?.id || null,
            lastDocId: lastDoc?.id || null
        }
    };
}






export async function exportLeadsToExcel(
    db: Firestore,
    user: User | null,
    request: FetchLeadsRequest,
    selectedColumns: string[]
): Promise<Uint8Array> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can export leads');
        }

        // Get user document
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        const isNoFilterOption = (filterValue: string) => {
            return filterValue === 'no-filter' || filterValue === 'null' || filterValue === 'empty';
        };

        // orderby
        // Determine which date field to use for ordering
        if (request?.orderByField) {
            orderBy(request?.orderByField, 'desc')
            console.log("Using orderBy field:", request?.orderByField);
        }
        // Create an array to store query constraints
        const buildQueryConstraints = () => {
            const queryConstraints: QueryConstraint[] = [];
            // const filterConditions: QueryCompositeFilterConstraint[] = [];

            if (request?.searchValue || request?.phoneValue) {
                if (request?.searchValue) {
                    const upperCaseQuery = request.searchValue.trim().toUpperCase();
                    console.log("upperCaseQuery", upperCaseQuery);
                    queryConstraints.push(
                        where('name', '>=', upperCaseQuery),
                        where('name', '<=', upperCaseQuery + '\uf8ff')
                    );
                }

                if (request?.phoneValue) {
                    const number = request.phoneValue.trim();
                    queryConstraints.push(
                        where('number', '>=', number),
                        where('number', '<=', number + '\uf8ff')
                    );
                }

                if (request.isActive) {
                    queryConstraints.push(where('isActive', '==', request?.isActive ?? request.isActive));
                }
            } else {
                if (request.isActive) {
                    queryConstraints.push(where('isActive', '==', request?.isActive ?? request.isActive));
                }

                if (request.activeFollowUpIds?.length === 1) {
                    if (isNoFilterOption(request.activeFollowUpIds[0])) {
                        // For "No Active Follow Up" - show leads with hasActiveFollowUp as false
                        queryConstraints.push(where('hasActiveFollowUp', '==', null));
                    } else {

                        if (request.activeFollowUpIds[0] === 'false') {
                            queryConstraints.push(where('hasActiveFollowUp', '==', false));

                        } else if (request.activeFollowUpIds[0] === 'true') {
                            queryConstraints.push(where('hasActiveFollowUp', '==', true));
                        }
                    }
                }

                // Add filters conditionally - checking for "No Filter" options and regular filters
                if (request.sourceIds && request.sourceIds.length > 0) {
                    console.log("sourceIds", request.sourceIds[0]);

                    if (isNoFilterOption(request.sourceIds[0])) {
                        // For "No Source" - show leads with null, empty string, or "0" sourceId
                        queryConstraints.push(where('sourceId', '==', null));
                    } else {
                        console.log("sourceIds", request.sourceIds);
                        queryConstraints.push(where('sourceId', 'in', request.sourceIds));
                    }
                }

                if (request.stateIds && request.stateIds.length > 0) {
                    console.log("stateIds", request.stateIds[0]);

                    if (isNoFilterOption(request.stateIds[0])) {
                        queryConstraints.push(where('stateId', '==', null));
                    } else {
                        queryConstraints.push(where('stateId', 'in', request.stateIds));
                    }
                }

                if (request.stageIds && request.stageIds.length > 0) {
                    if (isNoFilterOption(request.stageIds[0])) {
                        queryConstraints.push(where('stageId', '==', null));
                    } else {
                        queryConstraints.push(where('stageId', 'in', request.stageIds));
                    }
                }

                if (request.statusIds && request.statusIds.length > 0) {
                    if (isNoFilterOption(request.statusIds[0])) {
                        queryConstraints.push(where('statusId', '==', null));
                    } else {
                        queryConstraints.push(where('statusId', 'in', request.statusIds));
                    }
                }

                if (request.admissionStatusIds && request.admissionStatusIds.length > 0) {
                    if (isNoFilterOption(request.admissionStatusIds[0])) {
                        queryConstraints.push(where('admissionStatusId', '==', null));
                    } else {
                        queryConstraints.push(where('admissionStatusId', 'in', request.admissionStatusIds));
                    }
                }

                if (request.welcomeCallStatusIds && request.welcomeCallStatusIds.length > 0) {
                    if (isNoFilterOption(request.welcomeCallStatusIds[0])) {
                        queryConstraints.push(where('welcomeCallByAdmissionTeamStatusId', '==', null));
                    } else {
                        queryConstraints.push(where('welcomeCallByAdmissionTeamStatusId', 'in', request.welcomeCallStatusIds));
                    }
                }

                if (request.visaProcessStatusIds && request.visaProcessStatusIds.length > 0) {
                    if (isNoFilterOption(request.visaProcessStatusIds[0])) {
                        queryConstraints.push(where('visaFileProcessingStatusId', '==', null));
                    } else {
                        queryConstraints.push(where('visaFileProcessingStatusId', 'in', request.visaProcessStatusIds));
                    }
                }

                if (request.applicationStatusIds && request.applicationStatusIds.length > 0) {
                    if (isNoFilterOption(request.applicationStatusIds[0])) {
                        queryConstraints.push(where('applicationMovedStatusId', '==', null));
                    } else {
                        queryConstraints.push(where('applicationMovedStatusId', 'in', request.applicationStatusIds));
                    }
                }

                if (request.conditionalOfferLetterStatusIds && request.conditionalOfferLetterStatusIds.length > 0) {
                    if (isNoFilterOption(request.conditionalOfferLetterStatusIds[0])) {
                        queryConstraints.push(where('conditionalOfferLetterStatusId', '==', null));
                    } else {
                        queryConstraints.push(where('conditionalOfferLetterStatusId', 'in', request.conditionalOfferLetterStatusIds));
                    }
                }

                if (request.unconditionalOfferLetterStatusIds && request.unconditionalOfferLetterStatusIds.length > 0) {
                    if (isNoFilterOption(request.unconditionalOfferLetterStatusIds[0])) {
                        queryConstraints.push(where('unconditionalOfferLetterStatusId', '==', null));
                    } else {
                        queryConstraints.push(where('unconditionalOfferLetterStatusId', 'in', request.unconditionalOfferLetterStatusIds));
                    }
                }

                if (request.feePaymentStatusIds && request.feePaymentStatusIds.length > 0) {
                    if (isNoFilterOption(request.feePaymentStatusIds[0])) {
                        queryConstraints.push(where('feePaymentStatusId', '==', null));
                    } else {
                        queryConstraints.push(where('feePaymentStatusId', 'in', request.feePaymentStatusIds));
                    }
                }

                if (request.fileSubmissionStatusIds && request.fileSubmissionStatusIds.length > 0) {
                    if (isNoFilterOption(request.fileSubmissionStatusIds[0])) {
                        queryConstraints.push(where('fileSubmissionStatusId', '==', null));
                    } else {
                        queryConstraints.push(where('fileSubmissionStatusId', 'in', request.fileSubmissionStatusIds));
                    }
                }

                if (request.campaignIds && request.campaignIds.length > 0) {
                    console.log("campaignIds", request.campaignIds[0]);
                    if (isNoFilterOption(request.campaignIds[0])) {
                        queryConstraints.push(where('campaignId', '==', null));
                    } else {
                        queryConstraints.push(where('campaignId', 'in', request.campaignIds));
                    }
                }

                if (request.preferredStreamIds && request.preferredStreamIds.length > 0) {
                    if (isNoFilterOption(request.preferredStreamIds[0])) {
                        queryConstraints.push(where('preferredStreamId', '==', null));
                    } else {
                        queryConstraints.push(where('preferredStreamId', 'in', request.preferredStreamIds));
                    }
                }

                // super Agent
                if (request.superAgentId && request.superAgentId.length > 0) {
                    console.log("request.superAgentId", request.superAgentId);

                    if (request.superAgentId.length === 1) {
                        if (isNoFilterOption(request.superAgentId[0])) {
                            queryConstraints.push(where('superAgents', '==', null));
                        } else {
                            queryConstraints.push(where('superAgents', '==', request.superAgentId[0]));
                        }
                    } else if (request.superAgentId.length > 1) {
                        queryConstraints.push(where('superAgents', 'in', request.superAgentId));
                    }
                }

                // lead Admission SubStatus
                if (request.admissionSubStatus && request.admissionSubStatus.length > 0) {
                    if (isNoFilterOption(request.admissionSubStatus[0])) {
                        queryConstraints.push(where('subAdmissionStatusId', '==', null));
                    } else {
                        queryConstraints.push(where('subAdmissionStatusId', 'in', request.admissionSubStatus));
                    }
                }


                // lead Sub Sources
                if (request.leadSubSources && request.leadSubSources.length > 0) {
                    console.log("subsorce", request.leadSubSources);

                    if (isNoFilterOption(request.leadSubSources[0])) {
                        queryConstraints.push(where('subSourceId', '==', null));
                    } else {
                        queryConstraints.push(where('subSourceId', 'in', request.leadSubSources));
                    }
                }

                //  leadSubStages 
                if (request.leadSubStages && request.leadSubStages.length > 0) {
                    if (isNoFilterOption(request.leadSubStages[0])) {
                        queryConstraints.push(where('subStageId', '==', null));
                    } else {
                        queryConstraints.push(where('subStageId', 'in', request.leadSubStages));
                    }
                }

                //  Super Agents
                if (request.superAgentId && request.superAgentId.length > 0) {
                    console.log("request.superAgentId", request.superAgentId);

                    if (isNoFilterOption(request.superAgentId[0])) {
                        queryConstraints.push(where('superAgents', '==', null));
                    } else {
                        queryConstraints.push(where('superAgents', 'in', request.superAgentId));
                    }
                }
                

                //  leadSubStatus
                if (request.leadSubStatuses && request.leadSubStatuses.length > 0) {
                    if (isNoFilterOption(request.leadSubStatuses[0])) {
                        queryConstraints.push(where('subStatusId', '==', null));
                    } else {
                        queryConstraints.push(where('subStatusId', 'in', request.leadSubStatuses));
                    }
                }

                if (request.destinationIds && request.destinationIds.length > 0) {
                    if (isNoFilterOption(request.destinationIds[0])) {
                        queryConstraints.push(where('destinationId', '==', null));
                    } else {
                        queryConstraints.push(where('destinationId', 'in', request.destinationIds));
                    }
                }

                if (request.intakeMonth?.length === 1) {
                    console.log(">>>>>>>>", request.intakeMonth);
                    if (isNoFilterOption(request.intakeMonth[0])) {
                        // queryConstraints.push(where('intakeMonth', 'in', [null, '', '0', 0]));
                    } else {
                        queryConstraints.push(where('intakeMonth', '==', request.intakeMonth[0]));
                    }
                }

                if (request.intakeYear?.length === 1) {
                    console.log(">>>>>>>>", request.intakeYear);
                    const value = request.intakeYear[0];

                    if (isNoFilterOption(value)) {
                        // queryConstraints.push(where('intakeYear', 'in', [null, '', '0', 0]));
                    } else {
                        const numValue = Number(value);
                        const strValue = String(value);
                        // Use OR to match both number and string versions
                        queryConstraints.push(where('intakeYear', 'in', [numValue, strValue]));
                    }
                }

                // Handle date filtering for tranffertAt field
                if (request?.tranffertAtStartDate && request?.tranffertAtEndDate) {
                    queryConstraints.push(
                        where('transferredAt', '>=', request.tranffertAtStartDate),
                        where('transferredAt', '<=', request.tranffertAtEndDate)
                    );
                } else if (request?.tranffertAtStartDate) {
                    queryConstraints.push(where('transferredAt', '>=', request.tranffertAtStartDate));
                } else if (request?.tranffertAtEndDate) {
                    queryConstraints.push(where('transferredAt', '<=', request.tranffertAtEndDate));
                }

                // Handle date filtering for createdAt field
                if (request?.startDate && request?.endDate) {
                    queryConstraints.push(
                        where('createdAt', '>=', request.startDate),
                        where('createdAt', '<=', request.endDate)
                    );
                } else if (request?.startDate) {
                    queryConstraints.push(where('createdAt', '>=', request.startDate));
                } else if (request?.endDate) {
                    queryConstraints.push(where('createdAt', '<=', request.endDate));
                }

                // Handle date filtering for updatedAt field
                if (request?.updatedAtStartDate && request?.updatedAtEndDate) {
                    queryConstraints.push(
                        where('updatedAt', '>=', request.updatedAtStartDate),
                        where('updatedAt', '<=', request.updatedAtEndDate)
                    );
                } else if (request?.updatedAtStartDate) {
                    queryConstraints.push(where('updatedAt', '>=', request.updatedAtStartDate));
                } else if (request?.updatedAtEndDate) {
                    queryConstraints.push(where('updatedAt', '<=', request.updatedAtEndDate));
                }

                // handle date filtering for followUp date
                if (request?.followUpAtStartDate && request?.followUpAtEndDate) {
                    queryConstraints.push(
                        where('activeFollowUpTime', '>=', request.followUpAtStartDate),
                        where('activeFollowUpTime', '<=', request.followUpAtEndDate)
                    );
                } else if (request?.followUpAtStartDate) {
                    queryConstraints.push(where('activeFollowUpTime', '>=', request.followUpAtStartDate));
                } else if (request?.followUpAtEndDate) {
                    queryConstraints.push(where('activeFollowUpTime', '<=', request.followUpAtEndDate));
                }
            }

            if (request?.assignedTo) {
                console.log("1. assignedTo exists:", request.assignedTo);

                if (Array.isArray(request.assignedTo)) {
                    console.log("2. is array with length:", request.assignedTo.length);

                    if (request.assignedTo.length === 1) {
                        console.log("3a. Single item case:", request.assignedTo[0]);
                        if (isNoFilterOption(request.assignedTo[0])) {
                            queryConstraints.push(where('assignedTo', '==', null));
                        } else {
                            queryConstraints.push(where('assignedTo', '==', request.assignedTo[0]));
                        }
                    } else if (request.assignedTo.length > 1) {
                        console.log("3b. Multiple items case:", request.assignedTo);
                        queryConstraints.push(where('assignedTo', 'in', request.assignedTo));
                    }
                }
            }

            if (request?.subAgent) {
                console.log("1. subAgent exists:", request.subAgent);

                if (Array.isArray(request.subAgent)) {
                    console.log("2. is array with length:", request.subAgent.length);

                    if (request.subAgent.length === 1) {
                        console.log("3a. Single item case:", request.subAgent[0]);
                        if (isNoFilterOption(request.subAgent[0])) {
                            queryConstraints.push(where('subAgentId', '==', null));
                        } else {
                            queryConstraints.push(where('subAgentId', '==', request.subAgent[0]));
                        }
                    } else if (request.subAgent.length > 1) {
                        console.log("3b. Multiple items case:", request.subAgent);
                        queryConstraints.push(where('subAgentId', 'in', request.subAgent));
                    }
                }
            }

            if (request?.subAgentId) {
                console.log("1. subAgentId exists:", request.subAgentId);
                if (isNoFilterOption(request.subAgentId)) {
                    queryConstraints.push(where('subAgentId', '==', null));
                } else {
                    queryConstraints.push(where('subAgentId', '==', request.subAgentId));
                }
            }

            console.log("request>>>>>>>>>>>>>", request);


            return queryConstraints;
        };

        // Build base query queryConstraints
        const baseConstraints = buildQueryConstraints();

        // Add the appropriate ordering constraint
        const sortDir = request?.sortDirection || 'desc'; // Default to 'desc' if not specified
        const queryConstraintsWithOrder = [...baseConstraints, orderBy(request?.orderByField, sortDir)];
        // Build initial query with filters
        let leadsQuery = query(
            collection(db, `companies/${userData.companyId}/leads`),
            ...queryConstraintsWithOrder
        );

        const leadsSnapshot = await getDocs(leadsQuery);

        // Fetch reference data
        const sourcesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadSources`));
        const sources = sourcesSnapshot.docs.map(doc => ({ id: doc.id, source: doc.data().source || '' }));

        const subSourceSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadSubSources`));
        const subSources = subSourceSnapshot.docs.map(doc => ({ id: doc.id, subSource: doc.data().subSource || '' }));



        const campaignsSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadCampaigns`));
        const campaigns = campaignsSnapshot.docs.map(doc => ({ id: doc.id, campaign: doc.data().campaign || '' }));

        const assigneesQuery = query(collection(db, `users`), where('companyId', '==', userData.companyId));
        const assigneesSnapshot = await getDocs(assigneesQuery);
        const assignees = assigneesSnapshot.docs.map(doc =>
        ({
            id: doc.id, name: doc.data().name, email: doc.data().email
        })
        );

        const stagesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadStages`));
        const stages = stagesSnapshot.docs.map(doc => ({ id: doc.id, stage: doc.data().stage || '' }));

        const subStageSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadSubStages`));
        const subStages = subStageSnapshot.docs.map(doc => ({ id: doc.id, subStage: doc.data().subStage || '' }));

        const statusesSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadStatuses`));
        const statuses = statusesSnapshot.docs.map(doc => ({ id: doc.id, status: doc.data().status || '' }));

        const subStatusSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadSubStatuses`));
        const subStatuses = subStatusSnapshot.docs.map(doc => ({ id: doc.id, subStatus: doc.data().subStatus || '' }));

        const stateSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadStates`));
        const States = stateSnapshot.docs.map(doc => ({ id: doc.id, State: doc.data().state || '' }));

        const welcomeCallStatusSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadWelcomeCallByAdmissionTeamStatus`));
        const welcomeCallStatuses = welcomeCallStatusSnapshot.docs.map(doc => ({ id: doc.id, welcomeCallByAdmissionTeamStatus: doc.data().welcomeCallByAdmissionTeamStatus || '' }));

        const visaFileProcessingStatusSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadVisaFileProcessingStatus`));
        const visaFileProcessingStatuses = visaFileProcessingStatusSnapshot.docs.map(doc => ({ id: doc.id, visaFileProcessingStatus: doc.data().visaFileProcessingStatus || '' }));

        const applicationMovedStatusSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadApplicationMovedStatus`));
        const applicationMovedStatuses = applicationMovedStatusSnapshot.docs.map(doc => ({ id: doc.id, applicationMovedStatus: doc.data().applicationMovedStatus || '' }));

        const conditionalOfferLetterStatusSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadOfferLetterStatus`));
        console.log("conditionalOfferLetterStatusSnapshot", conditionalOfferLetterStatusSnapshot);

        const conditionalOfferLetterStatuses = conditionalOfferLetterStatusSnapshot.docs.map(doc => ({ id: doc.id, offerLetterStatus: doc.data().offerLetterStatus || '' }));
        console.log("conditionalOfferLetterStatuses", conditionalOfferLetterStatuses);


        const unconditionalOfferLetterStatusSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadOfferLetterStatus`));
        const unconditionalOfferLetterStatuses = unconditionalOfferLetterStatusSnapshot.docs.map(doc => ({ id: doc.id, offerLetterStatus: doc.data().offerLetterStatus || '' }));

        const feePaymentStatusSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadFeePaymentStatus`));
        const feePaymentStatuses = feePaymentStatusSnapshot.docs.map(doc => ({ id: doc.id, feePaymentStatus: doc.data().feePaymentStatus || '' }));

        const fileSubmissionStatusSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/leadFileSubmissionStatus`));
        const fileSubmissionStatuses = fileSubmissionStatusSnapshot.docs.map(doc => ({ id: doc.id, fileSubmissionStatus: doc.data().fileSubmissionStatus || '' }));

        const preferredStreamSnapshot = await getDocs(collection(db, `companies/${userData.companyId}/preferredStreams`));
        const preferredStreams = preferredStreamSnapshot.docs.map(doc => ({ id: doc.id, preferredStream: doc.data().preferredStream || '' }));



        const destinationsSnapshot = await getDocs(collection(db, `countries`));
        const destinations = destinationsSnapshot.docs.map(doc => ({
            id: doc.id,
            data: doc.data() as Destination
        }));

        const subAgentOptionsSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/subAgents`));
        const subAgentOptions = subAgentOptionsSnapshot.docs.map(doc => ({
            id: doc.id,
            data: doc.data() as SubAgent
        }));

        const admissionStatusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadAdmissionStatus`));
        const admissionStatuses = admissionStatusesSnapshot.docs.map(doc => ({
            id: doc.id,
            admissionStatus: doc.data().admissionStatus || ''
        }));

        const subAdmissionStatusSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadAdmissionSubStatus`));
        const subAdmissionStatuses = subAdmissionStatusSnapshot.docs.map(doc => ({
            id: doc.id,
            subAdmissionStatus: doc.data().subAdmissionStatus || ''
        }));

        const superAgentsSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/superAgents`));
        const superAgents = superAgentsSnapshot.docs.map(doc => ({
            id: doc.id,
            superAgent: doc.data().superAgent || ''
        }));

        console.log("Number of leads found:", leadsSnapshot.docs.length);
        console.log("subAdmissionStatuses", subAdmissionStatuses);

        // Check if any leads were found
        if (leadsSnapshot.docs.length === 0) {
            throw new Error('NO_LEADS_FOUND');
        }


        // Transform data for Excel
        const excelData = await Promise.all(leadsSnapshot.docs.map(async (doc, index) => {
            const leadData = doc.data() as LeadDocumentData;

            // Fetch follow-up data if exists
            let followUpText = '';
            if (selectedColumns.includes('Follow Up') && leadData.hasActiveFollowUp && leadData.activeFollowUpId && typeof leadData.activeFollowUpId === 'string') {
                try {
                    const followUpsQuery = query(
                        collection(db, `companies/${userData.companyId}/followUps`),
                        where(documentId(), '==', leadData.activeFollowUpId)
                    );
                    const followUpsSnapshot = await getDocs(followUpsQuery);
                    const followUp = followUpsSnapshot.docs[0]?.data() as FollowUp | undefined;
                    followUpText = followUp?.followUp || '';
                } catch (error) {
                    console.warn('Error fetching follow-up:', error);
                    followUpText = 'Error loading follow-up';
                }
            }

            // Fetch latest remark if requested
            let latestRemark = '';
            if (selectedColumns.includes('Remark')) {
                try {
                    const remarksRef = collection(db, `companies/${userData.companyId}/leads/${doc.id}/remarks`);
                    const remarksQuery = query(remarksRef, orderBy('createdAt', 'desc'), limit(1));
                    const remarksSnapshot = await getDocs(remarksQuery);

                    if (!remarksSnapshot.empty) {
                        const remarkData = remarksSnapshot.docs[0].data() as LeadRemark;
                        latestRemark = remarkData.remark || '';
                    }
                } catch (error) {
                    console.warn('Error fetching latest remark:', error);
                    latestRemark = 'Error loading remark';
                }
            }

            // Find reference data
            const source = sources.find(source => source.id === leadData.sourceId);
            const subSource = subSources.find(subSource => subSource.id === leadData.subSourceId);
            const campaign = campaigns.find(campaign => campaign.id === leadData.campaignId);
            const stage = stages.find(stage => stage.id === leadData.stageId);
            const substage = subStages.find(substage => substage.id === leadData.subStageId);
            const status = statuses.find(statuses => statuses.id === leadData.statusId);
            const subStatus = subStatuses.find(subStatus => subStatus.id === leadData.subStatusId);
            const assignee = assignees.find(assignee => assignee.id === leadData.assignedTo);
            const state = States.find(state => state.id === leadData.stateId);
            const welcomeCallStatus = welcomeCallStatuses.find(welcomeCallStatus => welcomeCallStatus.id === leadData.welcomeCallByAdmissionTeamStatusId);
            const visaFileProcessingStatus = visaFileProcessingStatuses.find(visaFileProcessingStatus => visaFileProcessingStatus.id === leadData.visaFileProcessingStatusId);
            const applicationMovedStatus = applicationMovedStatuses.find(applicationMovedStatus => applicationMovedStatus.id === leadData.applicationMovedStatusId);
            const conditionalOfferLetterStatus = conditionalOfferLetterStatuses.find(conditionalOfferLetterStatus => conditionalOfferLetterStatus.id === leadData.conditionalOfferLetterStatusId);
            const unconditionalOfferLetterStatus = unconditionalOfferLetterStatuses.find(unconditionalOfferLetterStatus => unconditionalOfferLetterStatus.id === leadData.unconditionalOfferLetterStatusId);
            const feePaymentStatus = feePaymentStatuses.find(feePaymentStatus => feePaymentStatus.id === leadData.feePaymentStatusId);
            const fileSubmissionStatus = fileSubmissionStatuses.find(fileSubmissionStatus => fileSubmissionStatus.id === leadData.fileSubmissionStatusId);
            const preferredStreamses = preferredStreams.find(preferredStreamses => preferredStreamses.id === leadData.preferredStreamId);
            const destination = destinations.find(destination => destination.id === leadData.destinationId)?.data?.name;
            const subAgent = subAgentOptions.find(subAgent => subAgent.id === leadData.subAgentId)?.data?.name
            const admissionStatuse = admissionStatuses.find((admissionStatus) => admissionStatus.id === leadData.admissionStatusId)
            const subAdmissionStatuse = subAdmissionStatuses.find((subAdmissionStatus) => subAdmissionStatus.id === leadData.subAdmissionStatusId)
            const superAgent = superAgents.find((superAgent) => superAgent.id === leadData.superAgents)

            console.log("leads>>>>>>>>>>>>", leadData.subAdmissionStatusId);

            console.log("reference data>>>>>", subAdmissionStatuse);
            console.log("reference AD>>>>>", admissionStatuse);


            // Create base object with all possible fields
            const allFields = {
                'Sl.No': index + 1,
                'Name': leadData.name || '',
                'Phone': leadData.number || '',
                'Created At': leadData.createdAt ? leadData.createdAt.toDate().toLocaleDateString() : '',
                'Updated At': leadData.updatedAt ? leadData.updatedAt.toDate().toLocaleDateString() : '',
                'Source': source?.source || '',
                'Sub Source': subSource?.subSource || '',
                'Campaign Name': campaign?.campaign || '',
                'Destination': destination || '',
                'Sub Agent': subAgent || '',
                'Assigned To': assignee?.name || '',
                'Admission Status': admissionStatuse?.admissionStatus || '',
                'Sub Admission Status': subAdmissionStatuse?.subAdmissionStatus || '',
                'Intake Month': leadData.intakeMonth || '',
                'Intake Year': leadData.intakeYear || '',
                'Stage': stage?.stage || '',
                'Sub Stage': substage?.subStage || '',
                'Priority': status?.status || '',
                'Follow Up': followUpText,
                'Remark': latestRemark,
                // Additional fields
                'State': state?.State || '',
                'Status': status?.status || '',
                'Sub Status': subStatus?.subStatus || '',
                'Welcome Call Status': welcomeCallStatus?.welcomeCallByAdmissionTeamStatus || '',
                'Visa Process Status': visaFileProcessingStatus?.visaFileProcessingStatus || '',
                'Application Status': applicationMovedStatus?.applicationMovedStatus || '',
                'Conditional Offer': conditionalOfferLetterStatus?.offerLetterStatus || '',
                'Unconditional Offer': unconditionalOfferLetterStatus?.offerLetterStatus || '',
                'Fee Payment Status': feePaymentStatus?.feePaymentStatus || '',
                'File Submission Status': fileSubmissionStatus?.fileSubmissionStatus || '',
                'Preferred Stream': preferredStreamses?.preferredStream || '',
                'Super Agents': superAgent?.superAgent || '',
                'Follow Up Date': leadData.activeFollowUpTime ? leadData.activeFollowUpTime.toDate().toLocaleDateString() : '',
            };
            console.log("allField>>>>>>>>>>>>", allFields);


            // Filter to only include selected columns

            const filteredData: Record<string, any> = {};
            selectedColumns.forEach(col => {
                if (col in allFields) {
                    filteredData[col] = allFields[col as keyof typeof allFields];
                }
            });

            return filteredData;
        }));

        // Create worksheet
        const ws = utils.json_to_sheet(excelData);

        // Set column widths and styles only if data exists
        if (excelData.length > 0) {
            const colWidths = [
                { wch: 8 },   // Sl.No
                { wch: 30 },   // Name
                { wch: 15 },   // Phone
                { wch: 15 },   // Created At
                { wch: 15 },   // Updated At
                { wch: 20 },   // Source
                { wch: 20 },   // Sub Source
                { wch: 25 },   // Campaign Name
                { wch: 15 },   // Destination
                { wch: 15 },   // Sub Agent
                { wch: 20 },   // Assigned To
                { wch: 20 },   // Admission Status
                { wch: 20 },   // sub Admission Status
                { wch: 15 },   // Intake Month
                { wch: 15 },   // Intake Year
                { wch: 20 },   // Stage
                { wch: 20 },   // sub stage
                { wch: 15 },   // Priority
                { wch: 40 },    // Follow Up
                { wch: 40 },    // Remark
                { wch: 15 },   // State
                { wch: 15 },   // Status
                { wch: 20 },   // Sub Status
                { wch: 20 },   // Welcome Call Status
                { wch: 20 },   // Visa Process Status
                { wch: 20 },   // Application Status
                { wch: 20 },   // Conditional Offer
                { wch: 20 },   // Unconditional Offer
                { wch: 20 },   // Fee Payment Status
                { wch: 20 },   // File Submission Status
                { wch: 20 },   // Preferred Stream

            ];
            ws['!cols'] = colWidths;

            // Set header styles safely
            const headers = ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1', 'H1', 'I1', 'J1'];
            headers.forEach(header => {
                if (ws[header]) {
                    ws[header].s = {
                        font: { bold: true },
                        fill: { fgColor: { rgb: 'FFFF00' } }
                    };
                }
            });
        } else {
            // Add message if no data
            utils.sheet_add_aoa(ws, [["No leads found matching your criteria"]], { origin: 'A1' });
            ws['!cols'] = [{ wch: 50 }]; // Set width for message
        }

        // Create workbook
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Leads');

        // Generate Excel file
        const excelBuffer = write(wb, { type: 'array', bookType: 'xlsx' });

        return new Uint8Array(excelBuffer);
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error exporting leads';
        
        // Don't log NO_LEADS_FOUND as an error since it's expected behavior
        if (errorMessage !== 'NO_LEADS_FOUND') {
            console.error('Error in exportLeadsToExcel:', error);
        }
        
        throw new Error(errorMessage);
    }
}



// function to add a new lead
export async function addNewLead(
    db: Firestore,
    user: User | null,
    leadData: LeadDocumentData,
    companyData?: { companyId: string | undefined },
): Promise<LeadData> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch leads');
        }

        let userDoc: any;

        if (companyData?.companyId && (leadData.subAgentId === user?.uid)) {
            userDoc = await getDoc(doc(db, 'companies', companyData.companyId, 'subAgents', leadData.subAgentId));
        } else {
            userDoc = await getDoc(doc(db, 'users', user.uid));
        }

        const userData = userDoc.data() as UserData;


        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Add new lead
        const newLeadDoc = await addDoc(collection(db, `companies/${userData.companyId}/leads`), {
            ...leadData,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            isActive: true
        });


        const newLeadSnapshot = await getDoc(newLeadDoc);
        const newLeadData = newLeadSnapshot.data() as LeadDocumentData;

        return {
            ...newLeadData,
            id: newLeadDoc.id,
            transferredAt: newLeadData.transferredAt ?? null
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error adding new lead';
        console.error('Error in addNewLead:', error);
        throw new Error(errorMessage);
    }
}

// function to update a lead
export async function updateLead(
    db: Firestore,
    user: User | null,
    leadId: string,
    leadData: LeadDocumentData,
    companyData?: { companyId: string | undefined },
): Promise<LeadData> {
    try {
        // Check authentication
        if (!user) {
            throw new Error('Only authenticated users can fetch leads');
        }

        let userDoc: any;

        if (companyData?.companyId && (leadData.subAgentId === user?.uid)) {
            userDoc = await getDoc(doc(db, 'companies', companyData.companyId, 'subAgents', leadData.subAgentId));
        } else {
            userDoc = await getDoc(doc(db, 'users', user.uid));
        }

        const userData = userDoc.data() as UserData;

        if (!userData || !userData.companyId) {
            throw new Error('No company associated with your account');
        }

        // Update lead
        const leadDoc = doc(db, `companies/${userData.companyId}/leads`, leadId);
        await updateDoc(leadDoc, {
            ...leadData,
            updatedAt: Timestamp.now()
        });

        const updatedLeadSnapshot = await getDoc(leadDoc);
        const updatedLeadData = updatedLeadSnapshot.data() as LeadDocumentData;

        return {
            ...updatedLeadData,
            id: leadId,
            transferredAt: updatedLeadData.transferredAt ?? null
        };

    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Error updating lead';
        console.error('Error in updateLead:', error);
        throw new Error(errorMessage);
    }
}